import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowDownLeft, ArrowUpRight, QrCode, CheckCircle, Copy, ArrowRightLeft } from "lucide-react";
import { useApp } from "@/App";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { formatAssetAmount } from "@/lib/amountFormat";
import { getCryptoIconFallback, getCryptoIconUrl } from "@/lib/cryptoIcons";

function parseMeta(metaJson) {
  if (!metaJson) return {};
  if (typeof metaJson === "object") return metaJson;
  try {
    const parsed = JSON.parse(metaJson);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function normalizeNetworkLabel(raw) {
  const text = String(raw || "").trim().toLowerCase();
  if (!text) return null;
  if (text.includes("trc20") || text.includes("trx") || text.includes("tron")) return "TRC20";
  if (text.includes("erc20") || text.includes("eth") || text.includes("ethereum")) return "ERC20";
  if (text.includes("bep20") || text.includes("bsc") || text.includes("bnb")) return "BEP20";
  if (text.includes("ton")) return "TON";
  if (text.includes("sol")) return "SOL";
  if (text.includes("btc") || text.includes("bitcoin")) return "BTC";
  return text.toUpperCase();
}

function parseFiatRequisites(rawAddress) {
  const parts = {};
  const raw = String(rawAddress || "");
  for (const chunk of raw.split(",")) {
    if (!chunk.includes(":")) continue;
    const [k, ...rest] = chunk.split(":");
    const key = String(k || "").trim().toLowerCase();
    const value = rest.join(":").trim();
    if (key) parts[key] = value;
  }
  const methodRaw = String(parts.method || "").trim().toLowerCase();
  const method = methodRaw.includes("sbp")
    ? "sbp"
    : methodRaw.includes("card")
      ? "card"
      : null;
  let rub = null;
  const rubRaw = String(parts.rub || "").trim();
  if (rubRaw) {
    const parsed = Number(rubRaw.replace(/\s+/g, "").replace(",", "."));
    if (Number.isFinite(parsed)) rub = parsed;
  }
  const bank = String(parts.bank || "").trim() || null;
  const phone = String(parts.phone || "").trim() || null;
  const card = String(parts.card || "").trim() || null;
  const name = String(parts.name || "").trim() || null;
  let requisitesDisplay = null;
  if (method === "sbp") {
    const lines = [];
    if (phone) lines.push(phone);
    if (bank) lines.push(bank);
    requisitesDisplay = lines.join("\n") || raw || null;
  } else if (method === "card") {
    requisitesDisplay = card || raw || null;
  } else if (raw) {
    requisitesDisplay = raw;
  }
  return {
    method,
    rub,
    bank,
    phone,
    card,
    name,
    requisitesDisplay,
    hasParsed: Object.keys(parts).length > 0,
  };
}

function mapBackendTxToUI(tx) {
  const id = tx.transaction_id ?? tx.id;
  const direction = String(tx.direction || "").toLowerCase();
  const amount = Number(tx.amount ?? tx.amount_usd ?? 0);
  const refType = String(tx.ref_type || "").toLowerCase();
  const meta = parseMeta(tx.meta_json);
  const walletRaw = tx.wallet_address || meta.wallet_address || meta.address || null;
  const fiatReq = parseFiatRequisites(walletRaw);

  let type = direction === "credit" ? "deposit" : "payment";
  if (refType.includes("fortune") || refType.includes("magic_cup")) {
    type = direction === "credit" || refType.includes("win") ? "fortune_win" : "fortune_spin";
  } else if (refType.includes("swap")) {
    type = "swap";
  } else if (direction !== "credit" && (refType.includes("withdraw") || refType.includes("send") || refType.includes("internal_transfer_out"))) {
    type = "withdrawal";
  }
  const isFiatWithdrawal =
    type === "withdrawal" &&
    (
      String(meta.method || "").toLowerCase() === "fiat" ||
      fiatReq.method !== null ||
      (walletRaw && /method\s*:/i.test(String(walletRaw)))
    );

  let createdAt = new Date();
  if (tx.created_at != null) {
    const v = tx.created_at;
    const n = Number(v);
    if (Number.isFinite(n) && n > 1e9) createdAt = new Date(n * 1000);
    else createdAt = new Date(v);
  }

  return {
    id,
    type,
    status: String(tx.status || "completed"),
    amount,
    currency: tx.currency || "USDT",
    wallet_address: isFiatWithdrawal ? null : walletRaw,
    network: isFiatWithdrawal ? null : normalizeNetworkLabel(String(
      tx.network ||
      meta.network ||
      meta.chain ||
      meta.network_name ||
      meta.chain_name ||
      meta.chainId ||
      meta.pay_network ||
      meta.blockchain ||
      ""
    )),
    is_fiat_withdrawal: isFiatWithdrawal,
    is_fiat_topup: refType === "fiat_topup_credit",
    fiat_method: fiatReq.method,
    fiat_requisites: fiatReq.requisitesDisplay,
    amount_rub: Number(tx.amount_rub ?? meta.rub_amount_exact ?? meta.rub_amount ?? fiatReq.rub) || null,
    txid: tx.txid || meta.txid || meta.transaction_hash || meta.hash || null,
    order_id: tx.order_id || meta.order_id || null,
    swap_from_currency: String(tx.from_currency || meta.from_currency || "").toUpperCase() || null,
    swap_from_amount: Number(tx.from_amount ?? meta.from_amount ?? 0) || null,
    swap_to_currency: String(tx.to_currency || meta.to_currency || "").toUpperCase() || null,
    swap_to_amount: Number(tx.to_amount ?? meta.to_amount ?? 0) || null,
    created_at: createdAt.toISOString(),
    ref_type: tx.ref_type,
    ref_id: tx.ref_id,
    meta_json: tx.meta_json,
    direction,
  };
}

function getTxDescription(tx, t) {
  const refType = String(tx.ref_type || "").toLowerCase();

  if (tx.type === "fortune_win" || refType.includes("fortune_win") || refType.includes("magic_cup_win")) {
    return t("tx.desc_fortune_win");
  }
  if (
    tx.type === "fortune_spin" ||
    refType.includes("fortune_bet") ||
    refType.includes("fortune_spin") ||
    refType.includes("magic_cup_bet")
  ) {
    return t("tx.desc_fortune_spin");
  }
  if (refType.includes("internal_transfer")) return "Внутренний перевод";
  if (tx.type === "withdrawal" && tx.is_fiat_withdrawal) return t("tx.desc_withdrawal_rub", "Вывод рублей по реквизитам");
  if (tx.type === "withdrawal") return t("tx.desc_withdrawal");
  if (tx.type === "deposit") return t(tx.is_fiat_topup ? "tx.deposit_rub" : "tx.desc_deposit");
  if (tx.type === "swap") return t("tx.desc_swap");
  if (tx.type === "payment") return t("tx.desc_qr_payment");
  return t("tx.desc_balance_operation");
}

function mapNetworkIconSymbol(network, currency) {
  const net = String(network || "").trim().toUpperCase();
  if (!net) return String(currency || "USDT").toUpperCase();
  if (net.includes("TRC") || net === "TRX") return "TRX";
  if (net.includes("ERC") || net === "ETH") return "ETH";
  if (net.includes("BEP") || net.includes("BSC") || net === "BNB") return "BNB";
  if (net.includes("TON")) return "TON";
  if (net.includes("SOL")) return "SOL";
  if (net.includes("BTC")) return "BTC";
  return String(currency || "USDT").toUpperCase();
}

export default function TransactionDetailPage() {
  const { t } = useTranslation();
  const { txId } = useParams();
  const navigate = useNavigate();
  const { api, haptic } = useApp();

  const [loading, setLoading] = useState(true);
  const [tx, setTx] = useState(null);

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/transactions/${txId}`);
        setTx(mapBackendTxToUI(res.data));
      } catch (err) {
        toast.error(t("tx.not_found"));
        navigate("/history");
      } finally {
        setLoading(false);
      }
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [txId]);

  const handleCopyId = async () => {
    haptic?.("light");
    try {
      await navigator.clipboard.writeText(String(tx?.id ?? ""));
      toast.success(t("tx.hash_copied"));
    } catch (_) {
      // ignore
    }
  };

  const handleCopyValue = async (value, successText = t("tx.hash_copied")) => {
    haptic?.("light");
    try {
      await navigator.clipboard.writeText(String(value || ""));
      toast.success(successText);
    } catch (_) {
      // ignore
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="spinner" />
      </div>
    );
  }

  if (!tx) return null;

  const TypeIcon =
    tx.type === "deposit" || tx.type === "fortune_win"
      ? ArrowDownLeft
      : tx.type === "withdrawal"
        ? ArrowUpRight
        : tx.type === "swap"
          ? ArrowRightLeft
          : QrCode;
  const typeLabel =
    tx.type === "deposit"
      ? t(tx.is_fiat_topup ? "tx.deposit_rub" : "tx.deposit")
      : tx.type === "withdrawal"
        ? (tx.is_fiat_withdrawal ? t("tx.withdrawal_rub", "Вывод рублей") : t("tx.withdrawal"))
        : tx.type === "fortune_spin"
          ? t("tx.fortune_spin")
          : tx.type === "fortune_win"
            ? t("tx.fortune_win")
            : tx.type === "swap"
              ? t("tx.swap")
              : t("tx.qr_payment") || t("tx.payment");
  const description = getTxDescription(tx, t);
  const isSwap = tx.type === "swap";
  const txCurrency = String(
    isSwap ? (tx.swap_to_currency || tx.currency || "USDT") : (tx.currency || "USDT")
  ).toUpperCase();
  const networkText = String(tx.network || "").toUpperCase();
  const networkIconSymbol = mapNetworkIconSymbol(networkText, txCurrency);
  const coinIconSymbol = txCurrency || "USDT";
  const txAmountText = formatAssetAmount(
    Math.abs(Number(isSwap ? (tx.swap_to_amount ?? tx.amount) : tx.amount) || 0),
    txCurrency,
    {
    minDecimals: txCurrency === "USDT" || txCurrency === "USDC" ? 2 : 0,
    maxDecimals: tx.is_fiat_topup ? 6 : undefined,
  });
  const statusCode = String(tx.status || "").toLowerCase();
  const isPending = statusCode === "pending" || statusCode === "processing" || statusCode === "created";
  const isFailed = statusCode === "failed" || statusCode === "rejected";
  const statusLabel = isPending
    ? (t("tx.status_pending") || "Pending")
    : isFailed
      ? (t("tx.status_failed") || "Failed")
      : t("tx.confirmed");
  const statusColor = isPending
    ? "text-yellow-600"
    : isFailed
      ? "text-red-600"
      : "text-green-500";
  const statusBg = isPending
    ? "bg-yellow-100 dark:bg-yellow-900/30"
    : isFailed
      ? "bg-red-100 dark:bg-red-900/30"
      : "bg-green-100 dark:bg-green-900/30";

  return (
    <div
      className="h-[100dvh] min-h-[100dvh] bg-background safe-top flex flex-col"
      data-testid="transaction-detail-page"
    >
      <div
        className="flex-1 overflow-y-auto overscroll-contain px-4 py-6 pb-[calc(7rem+var(--lp-bottom))] space-y-6 animate-fade-in"
        style={{ WebkitOverflowScrolling: "touch" }}
      >
      <div className="text-center">
        <div className={cn("w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4", "bg-[var(--lp-tile)]")}> 
          <TypeIcon
            className={cn(
              "w-8 h-8",
              tx.type === "deposit" || tx.type === "fortune_win"
                ? "text-green-500"
                : tx.type === "withdrawal"
                  ? "text-red-500"
                  : tx.type === "fortune_spin"
                    ? "text-purple-500"
                    : "text-blue-500"
            )}
          />
        </div>
        <h1 className="text-2xl font-bold">{typeLabel}</h1>
        <div className={cn("inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium mt-2", statusBg)}>
          <CheckCircle className={cn("w-4 h-4", statusColor)} />
          <span className={cn(statusColor)}>{statusLabel}</span>
        </div>
      </div>

      <div className="text-center">
        <span className="font-mono text-4xl font-bold">
          {tx.type === "deposit" || tx.type === "fortune_win" ? "+" : (tx.type === "swap" ? "" : "-")}
          {txAmountText}
        </span>
        <span className="text-xl text-muted-foreground ml-2">{txCurrency}</span>
      </div>

      <div className="lp-glass divide-y divide-[var(--lp-hair)]">
        <div className="p-4 flex justify-between items-center">
          <span className="text-muted-foreground">{t("tx.date")}</span>
          <span className="text-sm font-medium text-foreground">{new Date(tx.created_at).toLocaleString()}</span>
        </div>

        <div className="p-4 flex justify-between items-center">
          <span className="text-muted-foreground">{t("tx.id")}</span>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-foreground">{String(tx.id)}</span>
            <button onClick={handleCopyId} className="lp-press p-2 hover:bg-[var(--lp-tile)] rounded-lg transition-colors">
              <Copy className="w-4 h-4 text-muted-foreground" />
            </button>
          </div>
        </div>

        {tx.type === "deposit" && tx.order_id && (
          <div className="p-4 flex justify-between items-start gap-3">
            <span className="text-muted-foreground">Order ID</span>
            <div className="flex items-start gap-2 min-w-0 max-w-[68%]">
              <span className="text-sm font-medium text-foreground break-all whitespace-normal text-right">{tx.order_id}</span>
              <button
                onClick={() => handleCopyValue(tx.order_id, t("topup.copy_success"))}
                className="lp-press p-2 hover:bg-[var(--lp-tile)] rounded-lg transition-colors shrink-0"
              >
                <Copy className="w-4 h-4 text-muted-foreground" />
              </button>
            </div>
          </div>
        )}

        <div className="p-4 flex justify-between items-center">
          <span className="text-muted-foreground">{t("tx.description")}</span>
          <span className="text-sm font-medium text-foreground text-right">{description}</span>
        </div>

        {(tx.type === "deposit" || tx.type === "withdrawal") && (
          <div className="p-4 flex justify-between items-center gap-3">
            <span className="text-muted-foreground">{t("tx.coin") || "Coin"}</span>
            <div className="flex items-center gap-2">
              <img
                src={getCryptoIconUrl(coinIconSymbol)}
                alt={coinIconSymbol}
                className="w-5 h-5 rounded-full"
                loading="lazy"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = getCryptoIconFallback(coinIconSymbol);
                }}
              />
              <span className="text-sm font-medium text-foreground">{txCurrency}</span>
            </div>
          </div>
        )}

        {(tx.type === "deposit" || tx.type === "withdrawal") && tx.network && (
          <div className="p-4 flex justify-between items-center">
            <span className="text-muted-foreground">{t("tx.network") || "Network"}</span>
            <div className="flex items-center gap-2">
              <img
                src={getCryptoIconUrl(networkIconSymbol)}
                alt={networkIconSymbol}
                className="w-5 h-5 rounded-full"
                loading="lazy"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = getCryptoIconFallback(networkIconSymbol);
                }}
              />
              <span className="text-sm font-medium text-foreground">{networkText}</span>
            </div>
          </div>
        )}

        {tx.type === "withdrawal" && tx.is_fiat_withdrawal && tx.fiat_method && (
          <div className="p-4 flex justify-between items-center">
            <span className="text-muted-foreground">{t("tx.method", "Метод")}</span>
            <span className="text-sm font-medium text-foreground">
              {tx.fiat_method === "sbp" ? t("tx.method_sbp", "СБП") : t("tx.method_card", "Карта")}
            </span>
          </div>
        )}

        {(tx.is_fiat_topup || (tx.type === "withdrawal" && tx.is_fiat_withdrawal)) && tx.amount_rub != null && (
          <div className="p-4 flex justify-between items-center">
            <span className="text-muted-foreground">{t("tx.amount_rub", "Сумма в рублях")}</span>
            <span className="text-sm font-medium text-foreground">{Number(tx.amount_rub).toFixed(2)} ₽</span>
          </div>
        )}

        {tx.type === "withdrawal" && tx.is_fiat_withdrawal && tx.fiat_requisites && (
          <div className="p-4 flex justify-between items-start gap-3">
            <span className="text-muted-foreground">{t("tx.requisites", "Реквизиты")}</span>
            <div className="flex items-start gap-2 min-w-0 max-w-[68%]">
              <span className="text-sm font-medium text-foreground break-all whitespace-pre-line text-right">{tx.fiat_requisites}</span>
              <button
                onClick={() => handleCopyValue(tx.fiat_requisites, t("topup.copy_success"))}
                className="lp-press p-2 hover:bg-[var(--lp-tile)] rounded-lg transition-colors shrink-0"
              >
                <Copy className="w-4 h-4 text-muted-foreground" />
              </button>
            </div>
          </div>
        )}

        {(tx.type === "deposit" || tx.type === "withdrawal") && !tx.is_fiat_withdrawal && tx.wallet_address && (
          <div className="p-4 flex justify-between items-start gap-3">
            <span className="text-muted-foreground">{t("tx.wallet") || "Wallet"}</span>
            <div className="flex items-start gap-2 min-w-0 max-w-[68%]">
              <span className="text-sm font-medium text-foreground break-all whitespace-normal text-right">{tx.wallet_address}</span>
              <button
                onClick={() => handleCopyValue(tx.wallet_address, t("topup.copy_success"))}
                className="lp-press p-2 hover:bg-[var(--lp-tile)] rounded-lg transition-colors shrink-0"
              >
                <Copy className="w-4 h-4 text-muted-foreground" />
              </button>
            </div>
          </div>
        )}

        {(tx.type === "deposit" || tx.type === "withdrawal") && tx.txid && (
          <div className="p-4 flex justify-between items-start gap-3">
            <span className="text-muted-foreground">{t("tx.hash") || "TxID"}</span>
            <div className="flex items-start gap-2 min-w-0 max-w-[68%]">
              <span className="text-sm font-medium text-foreground break-all whitespace-normal text-right">{tx.txid}</span>
              <button
                onClick={() => handleCopyValue(tx.txid, t("tx.hash_copied"))}
                className="lp-press p-2 hover:bg-[var(--lp-tile)] rounded-lg transition-colors shrink-0"
              >
                <Copy className="w-4 h-4 text-muted-foreground" />
              </button>
            </div>
          </div>
        )}

        {tx.type === "swap" && tx.swap_from_currency && tx.swap_from_amount != null && (
          <div className="p-4 flex justify-between items-center">
            <span className="text-muted-foreground">{t("swap.from")}</span>
            <div className="flex items-center gap-2">
              <img
                src={getCryptoIconUrl(tx.swap_from_currency)}
                alt={tx.swap_from_currency}
                className="w-4 h-4 rounded-full"
                loading="lazy"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = getCryptoIconFallback(tx.swap_from_currency);
                }}
              />
              <span className="text-sm font-medium text-foreground">
                {formatAssetAmount(Math.abs(Number(tx.swap_from_amount) || 0), tx.swap_from_currency, { minDecimals: 0 })} {tx.swap_from_currency}
              </span>
            </div>
          </div>
        )}

        {tx.type === "swap" && tx.swap_to_currency && tx.swap_to_amount != null && (
          <div className="p-4 flex justify-between items-center">
            <span className="text-muted-foreground">{t("swap.to_estimated")}</span>
            <div className="flex items-center gap-2">
              <img
                src={getCryptoIconUrl(tx.swap_to_currency)}
                alt={tx.swap_to_currency}
                className="w-4 h-4 rounded-full"
                loading="lazy"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = getCryptoIconFallback(tx.swap_to_currency);
                }}
              />
              <span className="text-sm font-medium text-foreground">
                {formatAssetAmount(Math.abs(Number(tx.swap_to_amount) || 0), tx.swap_to_currency, { minDecimals: 0 })} {tx.swap_to_currency}
              </span>
            </div>
          </div>
        )}
      </div>
      </div>
    </div>
  );
}
